import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle } from 'lucide-react'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/common/ui/dialog'
import { Button } from '@/common/ui/button'
import { Alert, AlertDescription } from '@/common/ui/alert'
import type { OverdueInvoiceCheckResponse } from '../../type'

type OverdueInvoiceWarningModalProps = {
	data: OverdueInvoiceCheckResponse
}

export function OverdueInvoiceWarningModal({
	data,
}: OverdueInvoiceWarningModalProps) {
	const navigate = useNavigate()
	const [open, setOpen] = useState(false)
	const [dismissed, setDismissed] = useState(false)

	const isOverdue = data.daysOverdue !== null && data.daysOverdue > 0

	// Use sessionStorage to prevent showing on every refresh
	// Cleared when browser closes
	const storageKey = `invoice-dismissed-${data.oldestInvoiceCreatedAt}`

	useEffect(() => {
		// Check if already dismissed in this session
		const isDismissed = sessionStorage.getItem(storageKey) === 'true'
		setDismissed(isDismissed)

		// Show modal if has pending/overdue invoice and not dismissed
		const hasPendingInvoices = data.pendingCount > 0
		if (hasPendingInvoices && !isDismissed) {
			setOpen(true)
		}
	}, [data.pendingCount, data.oldestInvoiceCreatedAt, storageKey])

	const handleDismiss = () => {
		// Save to sessionStorage (cleared when browser closes)
		sessionStorage.setItem(storageKey, 'true')
		setDismissed(true)
		setOpen(false)
	}

	const handleOpenChange = (isOpen: boolean) => {
		// Save to sessionStorage whenever modal is closed (including X button)
		if (!isOpen) {
			sessionStorage.setItem(storageKey, 'true')
			setDismissed(true)
		}
		setOpen(isOpen)
	}

	const handleViewInvoices = () => {
		setOpen(false)
		navigate('/operator/crypto-invoices')
	}

	// Don't render if no pending invoices or already dismissed
	if (data.pendingCount === 0 || dismissed) {
		return null
	}

	const formatDate = (dateString: string | null) => {
		if (!dateString) return '-'
		return new Date(dateString).toLocaleDateString('th-TH', {
			year: 'numeric',
			month: 'long',
			day: 'numeric',
		})
	}

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent className="max-w-md">
				<DialogHeader>
					<div className="flex items-center gap-2">
						<AlertTriangle className="h-6 w-6 text-destructive" />
						<DialogTitle>
							{isOverdue
								? '🚨 บริการถูกปิดชั่วคราว'
								: 'แจ้งเตือน: ใบแจ้งหนี้ใกล้ครบกำหนด'}
						</DialogTitle>
					</div>
					<DialogDescription>
						{isOverdue
							? 'บริการถูกปิดเนื่องจากค้างชำระเกินกำหนด กรุณาชำระโดยด่วนเพื่อเปิดบริการอีกครั้ง'
							: 'มีใบแจ้งหนี้ที่ใกล้ครบกำหนดชำระ กรุณาตรวจสอบและดำเนินการ'}
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4">
					{isOverdue && (
						<Alert variant="destructive">
							<AlertTriangle className="h-4 w-4" />
							<AlertDescription>
								<strong>บริการปิดมาแล้ว {data.daysOverdue} วัน</strong>
							</AlertDescription>
						</Alert>
					)}

					{!isOverdue && data.daysLeft !== null && data.daysLeft > 0 && (
						<Alert>
							<AlertTriangle className="h-4 w-4" />
							<AlertDescription>
								เหลือเวลาอีก <strong>{data.daysLeft} วัน</strong>{' '}
								ก่อนบริการถูกปิด
							</AlertDescription>
						</Alert>
					)}

					<div className="space-y-2 rounded-lg border p-4">
						<div className="flex justify-between text-sm">
							<span className="text-muted-foreground">
								จำนวนใบแจ้งหนี้ค้าง:
							</span>
							<span className="font-medium">{data.pendingCount} รายการ</span>
						</div>
						<div className="flex justify-between text-sm">
							<span className="text-muted-foreground">ยอดรวมค้างชำระ:</span>
							<span className="font-semibold text-destructive">
								${data.totalPendingAmount}
							</span>
						</div>
						{data.oldestInvoiceCreatedAt && (
							<div className="flex justify-between text-sm">
								<span className="text-muted-foreground">
									ใบแจ้งหนี้เก่าสุด:
								</span>
								<span className="font-medium">
									{formatDate(data.oldestInvoiceCreatedAt)}
								</span>
							</div>
						)}
						<div className="flex justify-between text-sm">
							<span className="text-muted-foreground">ระยะเวลาผ่อนผัน:</span>
							<span className="font-medium">{data.graceDays} วัน</span>
						</div>
					</div>

					{isOverdue && (
						<p className="text-sm text-muted-foreground">
							🔴 <strong>บริการถูกระงับชั่วคราว</strong>{' '}
							กรุณาชำระใบแจ้งหนี้ทันทีเพื่อเปิดบริการอีกครั้ง ระยะเวลาผ่อนผัน{' '}
							{data.graceDays} วัน หากเกินกำหนดอาจมีผลกระทบต่อบัญชี
						</p>
					)}

					{!isOverdue && data.daysLeft !== null && data.daysLeft <= 3 && (
						<p className="text-sm text-muted-foreground">
							กรุณาชำระภายใน {data.daysLeft} วัน
							เพื่อป้องกันการปิดบริการชั่วคราว
						</p>
					)}
				</div>

				<DialogFooter className="gap-2 sm:gap-0">
					<Button variant="outline" onClick={handleDismiss}>
						ปิด
					</Button>
					<Button onClick={handleViewInvoices}>ดูใบแจ้งหนี้</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
